// Guided tours - global storefront embed (no container).
//
// Interactive spotlight tutorials for the key customer journeys (Parts Finder,
// My Garage, the store/branch selector, Returns, Sign in/Register). The engine
// lives in ../shared/tour-engine.js; the step scripts live in ../tours/.
//
// UX:
//   • A floating "Tours" launcher (bottom-left) on pages that have a tour; it
//     opens a small menu of the tours available here, with a ✓ on finished ones.
//   • A one-time, dismissible invitation the first time a customer lands on a
//     page with a tour ("New to My Garage? Take a 30-second tour").
//   • The branch-selector tour is offered the first time the store drawer opens.
//   • Deep link: ?tour=<id> starts a tour straight away (QA, help articles, emails).
//   • window.GWTours = { start(id), list(), reset() } for support/testing.
//
// Never interrupts: no invite while another tour runs, on checkout, or when the
// role feature `showTours` is false. Everything degrades to a no-op.

import { runTour, isTourActive, getTourState, setTourState, ICONS } from '../shared/tour-engine.js';
import { TOURS } from '../tours/index.js';

var LAUNCH_ID = 'gwl-launcher';
var STYLE_ID = 'gwl-styles';
var INVITED_THIS_LOAD = {};

var CSS = [
  '.gwl-wrap{position:fixed;left:20px;bottom:20px;z-index:2147482990;font-family:inherit;display:flex;flex-direction:column;align-items:flex-start;gap:10px;pointer-events:none}',
  '.gwl-wrap *{box-sizing:border-box}',
  '.gwl-btn{pointer-events:auto;display:inline-flex;align-items:center;gap:8px;height:44px;padding:0 16px 0 12px;border-radius:999px;border:0;cursor:pointer;background:#001689;color:#fff;font:inherit;font-size:13.5px;font-weight:700;letter-spacing:.01em;box-shadow:0 0 0 2px #fff,0 14px 30px -12px rgba(0,22,137,.75),0 2px 6px rgba(2,8,40,.18);transition:transform .2s cubic-bezier(.2,.8,.2,1),background .15s,box-shadow .2s;animation:gwl-in .5s cubic-bezier(.2,.8,.2,1) both}',
  '.gwl-btn:hover{background:#0b25b0;transform:translateY(-2px)}',
  '.gwl-btn:focus-visible{outline:2px solid #001689;outline-offset:3px}',
  '.gwl-btn svg{width:20px;height:20px}',
  '.gwl-badge{width:8px;height:8px;border-radius:50%;background:#ff4d55;box-shadow:0 0 0 2px #001689;margin-left:-4px;align-self:flex-start;margin-top:11px}',
  '@keyframes gwl-in{from{opacity:0;transform:translateY(12px) scale(.94)}to{opacity:1;transform:none}}',
  '.gwl-pop{pointer-events:auto;width:300px;background:#fff;border-radius:16px;box-shadow:0 26px 60px -14px rgba(2,8,40,.5),0 2px 6px rgba(2,8,40,.14);padding:8px;opacity:0;transform:translateY(8px) scale(.98);transform-origin:bottom left;transition:opacity .2s,transform .26s cubic-bezier(.2,.8,.2,1)}',
  '.gwl-pop.is-in{opacity:1;transform:none}',
  '.gwl-pop-h{padding:10px 12px 8px;font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#6b7387}',
  '.gwl-item{width:100%;display:flex;align-items:center;gap:12px;padding:10px 12px;border:0;background:transparent;border-radius:11px;cursor:pointer;text-align:left;font:inherit;color:#0b1435;transition:background .15s}',
  '.gwl-item:hover,.gwl-item:focus-visible{background:#f2f4fb;outline:none}',
  '.gwl-ic{flex:0 0 34px;height:34px;border-radius:8px;display:grid;place-items:center;background:#001689;color:#fff}',
  '.gwl-ic svg{width:18px;height:18px}',
  '.gwl-t{display:block;font-size:14px;font-weight:700;line-height:1.25}',
  '.gwl-s{display:block;font-size:12px;color:#6b7387;margin-top:2px}',
  '.gwl-done{margin-left:auto;flex:0 0 20px;height:20px;border-radius:50%;display:grid;place-items:center;background:#e7f7ee;color:#178a49}',
  '.gwl-done svg{width:12px;height:12px}',
  '.gwl-go{margin-left:auto;color:#9aa3b8}',
  '.gwl-go svg{width:16px;height:16px}',
  '.gwl-inv{pointer-events:auto;width:340px;max-width:calc(100vw - 40px);background:#fff;border-radius:18px;overflow:hidden;box-shadow:0 28px 64px -14px rgba(2,8,40,.55),0 2px 6px rgba(2,8,40,.14);opacity:0;transform:translateY(14px) scale(.97);transform-origin:bottom left;transition:opacity .3s,transform .4s cubic-bezier(.2,.8,.2,1)}',
  '.gwl-inv.is-in{opacity:1;transform:none}',
  '.gwl-inv-top{position:relative;overflow:hidden;padding:18px 18px 16px;background:#001689;color:#fff}',
  '.gwl-inv-top::after{content:"";position:absolute;left:0;right:0;bottom:0;height:4px;background:#d9272e}',
  '.gwl-inv-top::before{content:"";position:absolute;left:18px;bottom:0;width:46px;height:4px;background:#fff;transform:skewX(-30deg);z-index:1}',
  '.gwl-inv-k{position:relative;z-index:1;display:inline-flex;align-items:center;gap:6px;font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#c9d3ff}',
  '.gwl-inv-k svg{width:14px;height:14px}',
  '.gwl-wrap .gwl-inv-t{position:relative;z-index:1;margin:8px 0 4px!important;padding:0 24px 0 0;font-family:inherit;text-transform:none;font-size:18px!important;line-height:1.25;font-weight:800;letter-spacing:-.012em;color:#fff!important}',
  '.gwl-wrap .gwl-inv-b{position:relative;z-index:1;margin:0!important;font-size:13.5px;line-height:1.5;color:#dde3ff!important}',
  '.gwl-inv-x{position:absolute;z-index:2;right:10px;top:10px;width:28px;height:28px;border-radius:9px;border:0;background:rgba(255,255,255,.12);color:#fff;cursor:pointer;display:grid;place-items:center}',
  '.gwl-inv-x:hover{background:rgba(255,255,255,.22)}',
  '.gwl-inv-x svg{width:14px;height:14px}',
  '.gwl-inv-f{display:flex;align-items:center;gap:6px;padding:12px 12px 12px 16px}',
  '.gwl-inv-f .gwl-b{flex:0 0 auto}',
  '.gwl-inv-meta{margin-right:auto;font-size:12px;color:#6b7387;display:inline-flex;align-items:center;gap:6px;white-space:nowrap}',
  '.gwl-inv-meta svg{width:14px;height:14px}',
  '.gwl-b{appearance:none;white-space:nowrap;text-transform:none;letter-spacing:normal;border:0;border-radius:10px;padding:9px 14px;font:inherit;font-size:13.5px;font-weight:700;cursor:pointer;transition:background .15s,transform .1s}',
  '.gwl-b:active{transform:translateY(1px)}',
  '.gwl-b-ghost{background:transparent;color:#3d4659}',
  '.gwl-b-ghost:hover{background:#f1f3f9}',
  '.gwl-b-pri{background:#001689;color:#fff;box-shadow:0 8px 16px -8px rgba(0,22,137,.7)}',
  '.gwl-b-pri:hover{background:#0b25b0}',
  '.gwl-b:focus-visible,.gwl-item:focus-visible,.gwl-inv-x:focus-visible{outline:2px solid #3b5bdb;outline-offset:2px}',
  '.gwl-wrap.is-hidden{display:none}',
  '@media (max-width:640px){.gwl-wrap{left:12px;bottom:12px}.gwl-btn{width:46px;height:46px;padding:0;justify-content:center}.gwl-btn .gwl-label,.gwl-btn .gwl-badge{display:none}.gwl-inv{width:calc(100vw - 24px)}}',
  '@media (prefers-reduced-motion:reduce){.gwl-wrap *{transition:none!important;animation:none!important}}',
].join('\n');

function svg(name, extra) {
  return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="' + (extra || 2) + '" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (ICONS[name] || ICONS.compass) + '</svg>';
}
var COMPASS = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + ICONS.help + '</svg>';
var CLOCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>';
var CLOSE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>';
var CHEV = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 6l6 6-6 6"/></svg>';

function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }

function enabled() {
  try { if ((window.__PARTS_FINDER_FEATURES__ || {}).showTours === false) return false; } catch (e) { /* ignore */ }
  var p = location.pathname.toLowerCase();
  return !/^\/checkout(\/|$)/.test(p) && !/\/(payment|confirmation)(\/|$)/.test(p);
}

function safe(fn) { try { return fn(); } catch (e) { return false; } }
function pageTours() { return TOURS.filter(function (t) { return t.scope !== 'global' && safe(t.available); }); }
function menuTours() {
  var page = pageTours();
  var glob = TOURS.filter(function (t) { return t.scope === 'global' && safe(t.available); });
  return page.concat(glob);
}
function findTour(id) { return TOURS.find(function (t) { return t.id === id; }); }
function isDone(t) { return getTourState(t.id, t.version) === 'done'; }

function startTour(t) {
  if (!t) return Promise.resolve('missing');
  closePop(); closeInvite();
  hideLauncher(true);
  return runTour(t, { onEnd: function () { hideLauncher(false); render(); } });
}

// ── launcher ──────────────────────────────────────────────────────────────
var wrap = null, pop = null, invite = null;

function ensureWrap() {
  if (wrap && wrap.isConnected) return wrap;
  if (!document.getElementById(STYLE_ID)) {
    var s = document.createElement('style'); s.id = STYLE_ID; s.textContent = CSS;
    (document.head || document.documentElement).appendChild(s);
  }
  wrap = document.createElement('div');
  wrap.className = 'gwl-wrap';
  wrap.id = LAUNCH_ID;
  document.body.appendChild(wrap);
  return wrap;
}
function hideLauncher(h) { if (wrap) wrap.classList.toggle('is-hidden', !!h); }

function render() {
  if (!enabled() || isTourActive()) { if (wrap) wrap.innerHTML = ''; return; }
  var tours = pageTours();
  if (!tours.length) { if (wrap) wrap.innerHTML = ''; closePop(); return; }
  ensureWrap();
  if (wrap.querySelector('.gwl-btn')) return; // already showing
  var anyNew = tours.some(function (t) { return !getTourState(t.id, t.version); });
  var btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'gwl-btn';
  btn.setAttribute('aria-haspopup', 'menu');
  btn.setAttribute('aria-expanded', 'false');
  btn.setAttribute('aria-label', 'Guided tours');
  btn.innerHTML = COMPASS + '<span class="gwl-label">Tours</span>' + (anyNew ? '<span class="gwl-badge" aria-hidden="true"></span>' : '');
  btn.addEventListener('click', function (e) { e.stopPropagation(); if (pop) closePop(); else openPop(btn); });
  wrap.appendChild(btn);
}

function openPop(btn) {
  closeInvite();
  var list = menuTours();
  pop = document.createElement('div');
  pop.className = 'gwl-pop';
  pop.setAttribute('role', 'menu');
  pop.innerHTML = '<div class="gwl-pop-h">Quick tours</div>' + list.map(function (t) {
    return '<button type="button" role="menuitem" class="gwl-item" data-tour="' + esc(t.id) + '">' +
      '<span class="gwl-ic">' + svg(t.icon) + '</span><span><span class="gwl-t">' + esc(t.title) + '</span>' +
      '<span class="gwl-s">' + esc((t.duration || '30 sec') + ' · guided tour') + '</span></span>' +
      (isDone(t) ? '<span class="gwl-done" title="Completed">' + svg('check', 2.6) + '</span>' : '<span class="gwl-go">' + CHEV + '</span>') +
      '</button>';
  }).join('');
  wrap.insertBefore(pop, wrap.firstChild);
  btn.setAttribute('aria-expanded', 'true');
  requestAnimationFrame(function () { pop.classList.add('is-in'); });
  pop.addEventListener('click', function (e) {
    var it = e.target.closest && e.target.closest('[data-tour]');
    if (it) startTour(findTour(it.getAttribute('data-tour')));
  });
  var first = pop.querySelector('.gwl-item'); if (first) first.focus();
  setTimeout(function () { document.addEventListener('click', outside, true); document.addEventListener('keydown', escPop, true); }, 0);
}
function outside(e) { if (pop && !pop.contains(e.target) && !(wrap && wrap.querySelector('.gwl-btn') && wrap.querySelector('.gwl-btn').contains(e.target))) closePop(); }
function escPop(e) { if (e.key === 'Escape') { closePop(); var b = wrap && wrap.querySelector('.gwl-btn'); if (b) b.focus(); } }
function closePop() {
  document.removeEventListener('click', outside, true);
  document.removeEventListener('keydown', escPop, true);
  if (!pop) return;
  var p = pop; pop = null;
  var b = wrap && wrap.querySelector('.gwl-btn'); if (b) b.setAttribute('aria-expanded', 'false');
  p.classList.remove('is-in'); setTimeout(function () { p.remove(); }, 220);
}

// ── invitation ────────────────────────────────────────────────────────────
function showInvite(t) {
  if (!t || invite || isTourActive() || !enabled()) return;
  if (getTourState(t.id, t.version)) return;
  if (INVITED_THIS_LOAD[t.id]) return;
  INVITED_THIS_LOAD[t.id] = true;
  ensureWrap();
  var inv = t.invite || {};
  invite = document.createElement('div');
  invite.className = 'gwl-inv';
  invite.setAttribute('role', 'dialog');
  invite.setAttribute('aria-label', inv.title || t.title);
  invite.innerHTML =
    '<div class="gwl-inv-top"><button type="button" class="gwl-inv-x" data-a="later" aria-label="Dismiss">' + CLOSE + '</button>' +
    '<span class="gwl-inv-k">' + svg(t.icon) + esc(inv.kicker || 'Quick tour') + '</span>' +
    '<h4 class="gwl-inv-t">' + esc(inv.title || ('New to ' + t.title + '?')) + '</h4>' +
    '<p class="gwl-inv-b">' + esc(inv.body || 'Take a short guided tour of this page.') + '</p></div>' +
    '<div class="gwl-inv-f"><span class="gwl-inv-meta">' + CLOCK + esc(t.duration || '30 sec') + '</span>' +
    '<button type="button" class="gwl-b gwl-b-ghost" data-a="later">Maybe later</button>' +
    '<button type="button" class="gwl-b gwl-b-pri" data-a="go">Take the tour</button></div>';
  wrap.insertBefore(invite, wrap.firstChild);
  requestAnimationFrame(function () { invite.classList.add('is-in'); });
  invite.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('[data-a]');
    if (!a) return;
    if (a.getAttribute('data-a') === 'go') startTour(t);
    else { setTourState(t.id, t.version, 'dismissed'); closeInvite(); }
  });
}
function closeInvite() {
  if (!invite) return;
  var i = invite; invite = null;
  i.classList.remove('is-in'); setTimeout(function () { i.remove(); }, 300);
}

/** Wait for the page's "ready" selector, then a beat, then invite. */
function maybeInvite() {
  if (!enabled()) return;
  pageTours().forEach(function (t) {
    if (t.autoInvite === false || getTourState(t.id, t.version) || INVITED_THIS_LOAD[t.id]) return;
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      var ready = !t.readySelector || document.querySelector(t.readySelector);
      if (ready || tries > 40) {
        clearInterval(iv);
        if (ready && safe(t.available)) setTimeout(function () { showInvite(t); }, t.inviteDelay || 1400);
      }
    }, 250);
  });
}

// Offer the global branch-selector tour the first time the drawer opens.
function watchContextual() {
  TOURS.filter(function (t) { return t.contextual; }).forEach(function (t) {
    var shownOnce = false;
    var check = function () {
      if (shownOnce || isTourActive() || getTourState(t.id, t.version)) return;
      if (safe(t.contextual)) { shownOnce = true; setTimeout(function () { if (safe(t.contextual)) showInvite(t); }, 900); }
    };
    try { new MutationObserver(check).observe(document.body, { attributes: true, subtree: true, attributeFilter: ['class', 'style', 'aria-hidden'] }); } catch (e) { /* ignore */ }
  });
}

// SPA route changes (the Parts Finder app uses pushState).
var lastPath = '';
function watchRoute() {
  setInterval(function () {
    if (location.pathname === lastPath) return;
    lastPath = location.pathname;
    closePop(); closeInvite();
    if (wrap) wrap.innerHTML = '';
    render();
    maybeInvite();
  }, 700);
}

export function init() {
  if (!TOURS || !TOURS.length) return;
  lastPath = location.pathname;
  window.GWTours = {
    start: function (id) { return startTour(findTour(id)); },
    list: function () { return TOURS.map(function (t) { return { id: t.id, title: t.title, available: safe(t.available), state: getTourState(t.id, t.version) || 'new' }; }); },
    reset: function (id) { TOURS.forEach(function (t) { if (!id || t.id === id) { try { localStorage.removeItem('gwt:' + t.id + ':v' + (t.version || 1)); } catch (e) { /* ignore */ } } }); },
  };
  var forced = null;
  try { forced = new URLSearchParams(location.search).get('tour'); } catch (e) { /* ignore */ }
  if (forced && findTour(forced)) {
    var t = findTour(forced);
    var tries = 0;
    var iv = setInterval(function () {
      tries++;
      if (!t.readySelector || document.querySelector(t.readySelector) || tries > 40) { clearInterval(iv); startTour(t); }
    }, 250);
  } else {
    maybeInvite();
  }
  render();
  watchRoute();
  watchContextual();
}

export var id = 'guided-tours';
